<template>
  <div class="row">
    <div class="col-12">
      <StepHeading
        flowType="selectPackage"
        :currentStep="normalizedCurrentStep"
        :configData="configData"
        @go-to-step="handleGoToStep"
      />
    </div>
    <div class="col-12 col-xl-9">
      <Steps
        :key="`steps-${currentStepIndex}`"
        flowType="selectPackage"
        :currentStepId="currentStepId"
        :data="configData"
        :errors="errors"
        @update="updateData"
        @next="handleNextStep"
        @go-to-step="handleGoToStep"
        @reset-package="resetPackage"
        @select-base="handleBaseSelection"
        @printing-updated="onPrintingUpdated"
      />
    </div>
    <div class="col-12 col-xl-3">
      <Sidebar
        :key="`${configData.basePackage?.id || 'no-package'}-${configData.packaging?.id || 'no-packaging'}-${configData.quantity || 10}`"
        :data="configData"
        :currentStep="normalizedCurrentStep"
        flowType="selectPackage"
        :packageQuantity="configData.quantity"
        :cartItemKey="cartItemKey"
        @increase-product="increaseProduct"
        @decrease-product="decreaseProduct"
        @add-to-cart="handleAddToCart"
        @go-to-step="handleGoToStep"
        @reset-package="resetPackage"
        @remove-product="removeProduct"
        @remove-food="removeFoodDrink"
        @remove-packaging="removePackaging"
        @remove-card="removeCard"
        @update-products="updateProductsFromSidebar"
        @update-fooddrinks="updateFoodDrinksFromSidebar"
        @update:packageQuantity="updateQuantity"
        @clear-draft="clearDraft"
        @show-package-type-modal="handleShowPackageTypeModal"
      />
    </div>
  </div>

  <!-- Select package type modal -->
  <PackageTypeModal
    v-if="showPackageTypeModal === true"
    flowType="compose"
    :package-type-image-box="packageTypeImageBox || undefined"
    :package-type-image-mailbox="packageTypeImageMailbox || undefined"
    @confirm="handlePackageTypeConfirm"
    @close-modal="handleCloseModal"
  />

  <!-- Add to Cart modal -->
  <AddToCartModal
    v-if="showAddToCartModal"
    :packageData="addToCartModalData"
    :packageType="packageType"
    :isSaving="isSaving"
    @close-modal="handleCloseAddToCartModal"
    @open-package-type-modal="handleOpenPackageTypeModal"
  />

  <!-- Continue banner -->
  <ContinueBanner
    :show="showContinueBanner && cartItemKey === null"
    :summary="draftSummary"
    @continue="handleContinueDraft"
    @new="handleNewConfiguration"
    @dismiss="handleDismissBanner"
  />
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import StepHeading from './StepHeading.vue';
import Sidebar from './Sidebar/Sidebar.vue';
import Steps from './Steps.vue';
import PackageTypeModal from './modals/PackageTypeModal.vue';
import AddToCartModal from './modals/AddToCartModal.vue';
import ContinueBanner from './ContinueBanner.vue';
import { getFlowSteps, getStepByIndex } from './config/flowConfig.js';
import {
  increaseProductCount,
  decreaseProductCount,
  removeProduct as removeProductUtil,
  initializeProduct,
  cleanupUrlParameter,
  getUrlParameter,
  removePersonalization as removePersonalizationUtil,
} from './utils/productCountUtils.js';
import {
  calculateVatBreakdownByProduct,
  roundCurrency,
} from './utils/priceUtils.js';
import { useAutoSave } from '../../composables/useAutoSave.js';
import { useCartLinkInterceptor } from '../../composables/useCartLinkInterceptor.js';
import {
  clearAllCaches,
  clearServerCaches,
  forceRefresh,
  handleVersionChange,
} from './utils/cacheUtils.js';
import {
  determineModalContext,
  getContextOptions,
  initializePrefilledPackageTypeQuantity,
  MODAL_CONTEXT,
  clearFlowSwitch,
  markFlowSwitch,
} from './utils/contextAwareModal.js';

export default {
  name: 'SelectPackage',
  components: {
    AddToCartModal,
    StepHeading,
    Sidebar,
    Steps,
    PackageTypeModal,
    ContinueBanner,
  },
  setup() {
    const packageType = ref('large');
    const currentStepIndex = ref(0);
    const configData = ref({
      quantity: 10,
      packageType: 'large',
      isBasePackage: false,
      basePackage: null,
      products: [],
      foodDrinks: [],
      packaging: null,
      packagingOptions: [],
      packagingMode: 'box',
      packagingProduct: null,
      personalMessage: '',
      personalization: {
        productPersonalization: {},
        personalMessage: '',
      },
      deliveryInfo: {},
      isMailboxSession: false,
    });
    const errors = ref({});
    const selectingPackage = ref(false);
    const cartItemKey = ref(null);
    const showContinueBanner = ref(false);
    const showAddToCartModal = ref(false);
    const addToCartModalData = ref({});

    const modalContext = ref(null);
    const quantityFromUrlParam = ref(null); // Track if we got quantity from URL

    const flowType = ref('selectPackage');

    const packageTypeImageBox = computed(
      () =>
        (typeof window !== 'undefined' &&
          window.__S7_PACKAGE_TYPE_IMAGES__?.box) ||
        '',
    );
    const packageTypeImageMailbox = computed(
      () =>
        (typeof window !== 'undefined' &&
          window.__S7_PACKAGE_TYPE_IMAGES__?.mailbox) ||
        '',
    );

    const {
      isSaving,
      lastSaved,
      saveError,
      hasUnsavedChanges,
      showPackageTypeModal,
      clearDraft,
      getDraftSummary,
      restoreDraft,
    } = useAutoSave(configData, flowType, currentStepIndex);

    const draftSummary = ref(null);

    const updateDraftSummary = async () => {
      const summary = await getDraftSummary();
      draftSummary.value = summary;
    };

    const flowSteps = computed(() => {
      return getFlowSteps('selectPackage');
    });

    const totalSteps = computed(() => {
      return flowSteps.value.length - 1;
    });

    const currentStepId = computed(() => {
      const validStepIndex = Math.max(
        0,
        Math.min(currentStepIndex.value, totalSteps.value),
      );
      const step = getStepByIndex('selectPackage', validStepIndex);
      return step ? step.id : 'kies-basis';
    });

    const normalizedCurrentStep = computed(() => {
      return Math.max(0, currentStepIndex.value);
    });

    const checkForDraft = async () => {
      await updateDraftSummary();
      if (
        draftSummary.value &&
        draftSummary.value.flowType === 'selectPackage'
      ) {
        showContinueBanner.value = true;
      }
    };

    const handleContinueDraft = () => {
      if (restoreDraft()) {
        // Check if the restored draft was a mailbox package
        if (configData.value.packageType === 'small') {
          configData.value.isMailboxSession = true;
          sessionStorage.setItem('s7_is_mailbox_session', 'true');
        }
        showContinueBanner.value = false;
      }
    };

    const handleNewConfiguration = () => {
      clearDraft();
      showContinueBanner.value = false;
      // Re-evaluate context after clearing draft
      loadSavedPackageData();
    };

    const handleDismissBanner = () => {
      showContinueBanner.value = false;
    };

    const loadSessionData = () => {
      try {
        const sessionConfigStorage =
          sessionStorage.getItem('s7_current_config');
        const cartItemKeyStorage = sessionStorage.getItem('s7_cart_item_key');
        const isMailboxSession =
          sessionStorage.getItem('s7_is_mailbox_session') === 'true';

        if (!sessionConfigStorage) return;

        const parsedSessionData = JSON.parse(sessionConfigStorage);
        if (!validateConfigData(parsedSessionData)) return;

        const currentPackageType = configData.value.packageType;
        const currentQuantity = configData.value.quantity;

        configData.value = { ...configData.value, ...parsedSessionData };
        if (currentPackageType)
          configData.value.packageType = currentPackageType;
        if (currentQuantity) configData.value.quantity = currentQuantity;

        // Set mailbox session flag for filtering
        if (isMailboxSession) {
          configData.value.isMailboxSession = true;
          configData.value.packageType = 'small';
        }

        cartItemKey.value = cartItemKeyStorage
          ? parseInt(cartItemKeyStorage)
          : null;

        if (cartItemKey.value !== null) {
          const stored = localStorage.getItem('composedPackages');
          if (stored) {
            const existingPackages = JSON.parse(stored);
            if (
              cartItemKey.value >= 0 &&
              cartItemKey.value < existingPackages.length
            ) {
              const cartItem = existingPackages[cartItemKey.value];

              if (cartItem.products) {
                configData.value.products = cartItem.products.map(
                  (product) => ({
                    ...product,
                    count: product.quantity || product.count || 1,
                    quantity: product.quantity || product.count || 1,
                  }),
                );
              }

              if (cartItem.foodDrinks) {
                configData.value.foodDrinks = cartItem.foodDrinks.map(
                  (food) => ({
                    ...food,
                    count: food.quantity || food.count || 1,
                    quantity: food.quantity || food.count || 1,
                  }),
                );
              }

              if (cartItem.quantity)
                configData.value.quantity = cartItem.quantity;
              if (cartItem.packaging)
                configData.value.packaging = cartItem.packaging;
              if (cartItem.basePackage) {
                configData.value.basePackage = cartItem.basePackage;
                configData.value.isBasePackage = true;
              }
              if (cartItem.personalization)
                configData.value.personalization = cartItem.personalization;
            }
          }
        }

        const targetStep = sessionStorage.getItem('s7_target_step');
        const hash = window.location.hash.replace('#', '');
        const stepMap = {
          products: 1,
          foodDrinks: 2,
          packaging: 3,
          personalization: 4,
        };

        if (targetStep && stepMap[targetStep] !== undefined) {
          currentStepIndex.value = stepMap[targetStep];
        } else if (hash && stepMap[hash] !== undefined) {
          currentStepIndex.value = stepMap[hash];
        }

        // Don't remove cart_item_key yet - we need it to detect edit mode
        sessionStorage.removeItem('s7_current_config');
        sessionStorage.removeItem('s7_target_step');

        // Trigger product reload to apply mailbox filtering
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('s7_reload_products', {
              detail: { packageType: configData.value.packageType },
            }),
          );
        }, 100);
      } catch (error) {
        // Silent fail for production
      }
    };

    const reEvaluateContext = () => {
      const contextOptions = getContextOptions(configData.value);
      const contextResult = determineModalContext(contextOptions);
      modalContext.value = contextResult.context;
      showPackageTypeModal.value = contextResult.showModal;
      return contextResult;
    };

    const isEditMode = () => {
      return cartItemKey.value !== null && cartItemKey.value >= 0;
    };

    const setMailboxSession = () => {
      configData.value.isMailboxSession = true;
      configData.value.packageType = 'small';
      packageType.value = 'small';
      sessionStorage.setItem('s7_is_mailbox_session', 'true');
      sessionStorage.setItem('s7_package_type', 'small');
    };

    const loadSavedPackageData = () => {
      const contextOptions = getContextOptions(configData.value);
      const contextResult = determineModalContext(contextOptions);
      modalContext.value = contextResult.context;

      if (
        contextResult.context === MODAL_CONTEXT.FROM_SCRATCH ||
        contextResult.context === MODAL_CONTEXT.FLOW_SWITCH
      ) {
        configData.value.products = [];
        configData.value.foodDrinks = [];
        configData.value.packaging = null;
        configData.value.basePackage = null;
        configData.value.isBasePackage = false;
        sessionStorage.removeItem('s7_package_type');
        sessionStorage.removeItem('s7_package_quantity');
      }

      const savedType = sessionStorage.getItem('s7_package_type');
      const savedQuantity = sessionStorage.getItem('s7_package_quantity');
      const isMailboxSession =
        sessionStorage.getItem('s7_is_mailbox_session') === 'true';

      if (savedType) {
        packageType.value = savedType;
        configData.value.packageType = savedType;
      }

      if (!quantityFromUrlParam.value) {
        const aantalParam = getUrlParameter('aantal');
        if (aantalParam) {
          const quantity = parseInt(aantalParam);
          if (quantity > 0) {
            quantityFromUrlParam.value = quantity;
            configData.value.quantity = quantity;
            sessionStorage.setItem('s7_package_quantity', String(quantity));
            cleanupUrlParameter('aantal');
          }
        }
      }

      if (quantityFromUrlParam.value) {
        configData.value.quantity = quantityFromUrlParam.value;
      } else if (savedQuantity) {
        configData.value.quantity = parseInt(savedQuantity);
      }

      if (isMailboxSession) {
        configData.value.isMailboxSession = true;
      }

      const sizeParam = getUrlParameter('size');
      if (sizeParam === 'small') {
        setMailboxSession();
        cleanupUrlParameter('size');
      }

      if (
        contextResult.context === MODAL_CONTEXT.PREFILLED_BASE_PACKAGE &&
        contextResult.needsTypeQuantityInit
      ) {
        const { packageType: initType, quantity: initQuantity } =
          initializePrefilledPackageTypeQuantity(
            configData.value,
            configData.value.basePackage,
          );
        configData.value.packageType = initType;
        if (!quantityFromUrlParam.value) {
          configData.value.quantity = initQuantity;
        }
        packageType.value = initType;
      }

      showPackageTypeModal.value = contextResult.showModal;

      if (contextResult.context === MODAL_CONTEXT.RESUME_VIA_BANNER) {
        setTimeout(checkForDraft, 0);
      }
    };

    const showError = (message) => {
      errors.value.general = message;
      setTimeout(() => {
        errors.value.general = '';
      }, 5000);
    };

    const validateConfigData = (data) => {
      if (!data || typeof data !== 'object') {
        return false;
      }

      if (
        data.quantity &&
        (isNaN(data.quantity) || data.quantity < 10 || data.quantity > 99999)
      ) {
        return false;
      }

      if (data.products && !Array.isArray(data.products)) {
        return false;
      }

      if (data.foodDrinks && !Array.isArray(data.foodDrinks)) {
        return false;
      }

      return true;
    };

    const updateData = (newData) => {
      showContinueBanner.value = false;

      if (newData.removeProductFromSelection) {
        const productId = newData.removeProductFromSelection;
        configData.value.products = configData.value.products.filter(
          (product) => product.id !== productId,
        );
        delete newData.removeProductFromSelection;
      }

      for (const key in newData) {
        if (
          Array.isArray(newData[key]) &&
          Array.isArray(configData.value[key])
        ) {
          configData.value[key] = [...newData[key]];
        } else {
          configData.value[key] = newData[key];
        }
      }
      errors.value = {};
    };

    const onPrintingUpdated = (printingData) => {
      try {
        if (!printingData || typeof printingData !== 'object') return;

        if (!configData.value.personalization)
          configData.value.personalization = {};

        configData.value.personalization.printing = {
          counts: printingData.counts,
          breakdown: printingData.breakdown,
          total_ex_vat: printingData.breakdown.totalExVat,
        };

        errors.value = {};
      } catch (error) {
        // Silent fail for production
      }
    };

    const updateProductsFromSidebar = (newProducts) => {
      configData.value.products = [...newProducts];
    };

    const updateFoodDrinksFromSidebar = (newFoodDrinks) => {
      configData.value.foodDrinks = [...newFoodDrinks];
    };

    const handleNextStep = () => {
      nextStep();
    };

    const nextStep = () => {
      if (currentStepIndex.value < totalSteps.value) {
        currentStepIndex.value++;
        setTimeout(() => {
          const container = document.getElementById('scrollContainer');
          if (container) container.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    };

    const previousStep = () => {
      if (currentStepIndex.value > -1) {
        currentStepIndex.value--;
        setTimeout(() => {
          const container = document.getElementById('scrollContainer');
          if (container) container.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    };

    const handleGoToStep = (componentStepIndex) => {
      if (
        typeof componentStepIndex === 'number' &&
        componentStepIndex >= -1 &&
        componentStepIndex <= totalSteps.value
      ) {
        currentStepIndex.value = componentStepIndex;
        if (componentStepIndex === -1) errors.value = {};
        setTimeout(() => {
          const container = document.getElementById('scrollContainer');
          if (container) container.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    };

    const increaseProduct = (productId) => {
      configData.value.products = increaseProductCount(
        configData.value.products,
        { id: productId },
      );
    };

    const decreaseProduct = (productId) => {
      configData.value.products = decreaseProductCount(
        configData.value.products,
        productId,
      );
    };

    const removeProduct = (productId) => {
      configData.value.products = removeProductUtil(
        configData.value.products,
        productId,
      );
      configData.value.personalization.productPersonalization =
        removePersonalizationUtil(
          configData.value.personalization.productPersonalization,
          productId,
        );
    };

    const removeFoodDrink = (foodId) => {
      configData.value.foodDrinks = removeProductUtil(
        configData.value.foodDrinks,
        foodId,
      );
      configData.value.personalization.productPersonalization =
        removePersonalizationUtil(
          configData.value.personalization.productPersonalization,
          foodId,
        );
    };

    const removePackaging = () => {
      configData.value.packaging = null;
      configData.value.packagingMode = 'box';
      configData.value.packagingProduct = null;
    };

    const removeCard = () => {
      try {
        configData.value.personalization.selectedCard = null;
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het verwijderen van de kaart.',
        );
      }
    };

    const handleAddToCart = () => {
      const packageData = {
        packageName: configData.value.basePackage?.title || 'Gekozen pakket',
        basePackage: configData.value.basePackage || null,
        isBasePackage: configData.value.isBasePackage || false,
        packageType: configData.value.packageType,
        quantity: configData.value.quantity,
        s7_is_mailbox_package: configData.value.packageType === 'small',
        products: configData.value.products
          .filter((product) => (product.count || 0) > 0)
          .map((product) => ({
            ...product,
            quantity: product.count || product.quantity || 1,
            count: product.count || product.quantity || 1,
            taxClass: product.taxClass,
            taxRate: product.taxRate,
            taxLabel: product.taxLabel,
          })),
        foodDrinks: configData.value.foodDrinks
          .filter((food) => (food.count || 0) > 0)
          .map((food) => ({
            ...food,
            quantity: food.count || food.quantity || 1,
            count: food.count || food.quantity || 1,
            taxClass: food.taxClass,
            taxRate: food.taxRate,
            taxLabel: food.taxLabel,
          })),
        packaging: (() => {
          if (
            configData.value.packagingProduct &&
            !configData.value.packaging
          ) {
            return configData.value.packagingProduct;
          }

          return configData.value.packaging;
        })(),
        packagingOptions: configData.value.packagingOptions,
        packagingMode: configData.value.packagingMode || 'box',
        packagingProduct: configData.value.packagingProduct || null,
        personalization: {
          printworkCount: configData.value.products.filter(
            (p) => p.printwork && (p.count || 0) > 0,
          ).length,
          customDesign: !!configData.value.personalMessage,
          customDesignPrice: configData.value.personalMessage ? 2.98 : 0,
          productPersonalization:
            configData.value.personalization?.productPersonalization || {},
          selectedCard: (() => {
            const card = configData.value.personalization?.selectedCard;
            if (card) {
              // Preserve original price when adding to cart if not already set
              return {
                ...card,
                originalPrice: card.originalPrice || card.price,
              };
            }
            return null;
          })(),
          printNotes: configData.value.personalization?.printNotes || '',
          printing: {
            ...configData.value.personalization?.printing,
            per_package:
              configData.value.personalization?.printing?.per_package,
          },
          logoAttachments:
            configData.value.personalization?.logoAttachments || [],
        },
        deliveryInfo: configData.value.deliveryInfo,
        timestamp: Date.now(),
      };

      const stored = localStorage.getItem('composedPackages');
      const existingPackages = stored ? JSON.parse(stored) : [];

      if (
        cartItemKey.value !== null &&
        cartItemKey.value >= 0 &&
        cartItemKey.value < existingPackages.length
      ) {
        existingPackages[cartItemKey.value] = packageData;
      } else {
        existingPackages.push(packageData);
      }

      localStorage.setItem(
        'composedPackages',
        JSON.stringify(existingPackages),
      );
      sessionStorage.removeItem('s7_package_type');
      sessionStorage.removeItem('s7_package_quantity');

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });

        const items = [];
        const basePackageId = packageData.basePackage?.id || 0;
        const basePackageName =
          packageData.basePackage?.name ||
          packageData.packageName ||
          'Onbekend pakket';

        const packageQuantity = packageData.quantity || 1;

        const filteredProducts = (packageData.products || []).filter(
          (product) => {
            if (
              (packageData.packaging &&
                packageData.packaging.id === product.id) ||
              (packageData.packagingProduct &&
                packageData.packagingProduct.id === product.id)
            ) {
              return false;
            }
            return true;
          },
        );

        const allProducts = [
          ...filteredProducts,
          ...(packageData.foodDrinks || []),
          ...(packageData.packaging ? [packageData.packaging] : []),
        ].filter(Boolean);

        allProducts.forEach((product) => {
          if (!product.id || !product.name) {
            return;
          }

          const productQuantityPer = product.quantity || product.count || 1;
          const productQuantity = productQuantityPer * packageQuantity;
          const productPrice = parseFloat(product.price || 0);

          items.push({
            item_id: product.id,
            item_name: product.name,
            quantity: productQuantity,
            price: productPrice,
            item_list_id: basePackageId,
            item_list_name: basePackageName,
            quantity_item_list: packageQuantity,
            quantity_per: productQuantityPer,
          });
        });

        let totalValue = items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        let printingCostPerPackage = 0;
        if (packageData.personalization?.printing?.per_package?.total_ex_vat) {
          printingCostPerPackage =
            packageData.personalization.printing.per_package.total_ex_vat;
        } else if (packageData.personalization?.printing?.total_ex_vat) {
          printingCostPerPackage =
            packageData.personalization.printing.total_ex_vat / packageQuantity;
        }

        const totalPrintingCost = printingCostPerPackage * packageQuantity;

        if (totalPrintingCost > 0) {
          items.push({
            item_id: 'printing',
            item_name: 'Bedrukking',
            quantity: packageQuantity,
            price: printingCostPerPackage,
            item_list_id: basePackageId,
            item_list_name: basePackageName,
            quantity_item_list: packageQuantity,
            quantity_per: 1,
          });
        }

        totalValue += totalPrintingCost;

        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'EUR',
            value: totalValue,
            items: items,
          },
        });
      }

      clearDraft();

      if (isEditMode()) {
        // Clear cart item key now that we've saved
        sessionStorage.removeItem('s7_cart_item_key');
        setTimeout(() => {
          window.location.href = '/winkelmandje';
        }, 200);
      } else {
        showAddToCartModal.value = true;
        addToCartModalData.value = packageData;
        resetPackage(false);
      }
    };

    const resetPackage = (confirmDialog = true) => {
      try {
        if (
          confirmDialog &&
          !confirm('Weet je zeker dat je alles wilt resetten?')
        ) {
          return;
        }

        configData.value = {
          quantity: 10,
          packageType: 'large',
          isBasePackage: false,
          basePackage: null,
          products: [],
          foodDrinks: [],
          packaging: null,
          packagingOptions: [],
          packagingMode: 'box',
          packagingProduct: null,
          personalMessage: '',
          personalization: { productPersonalization: {}, personalMessage: '' },
          deliveryInfo: {},
        };

        packageType.value = 'large';
        currentStepIndex.value = 0;
        errors.value = {};
        sessionStorage.removeItem('s7_package_type');
        sessionStorage.removeItem('s7_package_quantity');
        sessionStorage.removeItem('s7_is_mailbox_session');

        // Clear all caches when resetting
        clearAllCaches();

        if (confirmDialog) {
          loadSavedPackageData();
        }
      } catch (error) {
        showError(
          'Er is een probleem opgetreden bij het resetten van je pakket.',
        );
      }
    };

    const handlePackageTypeConfirm = (packageType, quantity) => {
      configData.value.packageType = packageType;
      configData.value.quantity = quantity;

      sessionStorage.setItem('s7_package_type', packageType);
      sessionStorage.setItem('s7_package_quantity', String(quantity));

      if (packageType === 'small') {
        setMailboxSession();
      } else {
        configData.value.isMailboxSession = false;
        sessionStorage.removeItem('s7_is_mailbox_session');
      }

      clearFlowSwitch();

      // Close modal immediately for better UX
      showPackageTypeModal.value = false;

      // Dispatch events asynchronously to prevent blocking
      requestAnimationFrame(() => {
        reloadStepDataWithFilter();

        // Dispatch reload event to update ProductSelector
        document.dispatchEvent(
          new CustomEvent('s7_reload_products', { detail: { packageType } }),
        );
      });
    };

    const reloadStepDataWithFilter = () => {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        try {
          document.dispatchEvent(
            new CustomEvent('s7_reload_products', {
              detail: { packageType: configData.value.packageType },
            }),
          );
        } catch (error) {
          console.error('Error in reloadStepDataWithFilter:', error);
        }
      });
    };

    const updateQuantity = (newQuantity) => {
      configData.value.quantity = newQuantity;
    };

    const handleCloseAddToCartModal = () => {
      clearDraft();
      resetPackage(false);
      showAddToCartModal.value = false;
      addToCartModalData.value = {};
    };

    const handleOpenPackageTypeModal = async () => {
      try {
        // Close the current modal first
        showAddToCartModal.value = false;

        // Small delay to ensure modal closes before opening new one
        await new Promise((resolve) => setTimeout(resolve, 100));

        clearDraft();
        sessionStorage.removeItem('s7_current_config');
        sessionStorage.removeItem('s7_cart_item_key');
        sessionStorage.removeItem('s7_target_step');
        resetPackage(false);

        // Open package type modal for new package
        showPackageTypeModal.value = true;
      } catch (error) {
        console.error('Error opening package type modal:', error);
        // Fallback: just close the current modal
        showAddToCartModal.value = false;
      }
    };

    const handleCloseModal = () => {
      showPackageTypeModal.value = false;
    };

    const handleShowPackageTypeModal = () => {
      showPackageTypeModal.value = true;
    };

    const checkForFlowSwitch = () => {
      // Check if there's a compose flow draft when entering selectPackage flow
      const draft = localStorage.getItem('s7_configuration_draft');
      if (draft) {
        try {
          const draftData = JSON.parse(draft);
          // If there's a compose flow draft, mark flow switch to show modal
          if (draftData.flowType === 'compose') {
            markFlowSwitch();
          }
        } catch (error) {
          console.error('Error parsing draft data:', error);
        }
      }
    };

    const handleBaseSelection = async (pakket) => {
      if (selectingPackage.value || !pakket?.id) return;

      showContinueBanner.value = false;
      selectingPackage.value = true;

      const originalConfigData = { ...configData.value };
      configData.value.basePackage = {
        ...pakket,
        title: pakket.title || pakket.name || 'Pakket',
        products: [],
        foodDrinks: [],
        packaging: pakket.packaging || null,
      };

      try {
        if (
          window.globalProcessedPackageCache &&
          window.globalProcessedPackageCache[pakket.id]
        ) {
          const processedData = window.globalProcessedPackageCache[pakket.id];
          configData.value.products = processedData.allProducts;
          configData.value.foodDrinks = processedData.foodDrinks;
          configData.value.packaging = processedData.packaging;
          configData.value.isBasePackage = true;
          configData.value.basePackage = {
            ...pakket,
            title: pakket.title || pakket.name || 'Pakket',
            products: processedData.allProducts,
            foodDrinks: processedData.foodDrinks,
            packaging: processedData.packaging,
          };

          if (pakket.size === 'Brievenbus') {
            setMailboxSession();
          }

          cleanupUrlParameter('pakket');
          nextTick(() => {
            nextStep();
          });
          selectingPackage.value = false;
          return;
        }

        const pakketPromise =
          window.globalPackageCache && window.globalPackageCache[pakket.id]
            ? Promise.resolve({
                ok: true,
                json: async () => window.globalPackageCache[pakket.id],
              })
            : fetch(`/api/packages/${pakket.id}`).then(async (res) => {
                if (!res.ok && res.status === 429) {
                  await new Promise((resolve) => setTimeout(resolve, 3000));
                  return fetch(`/api/packages/${pakket.id}`);
                }
                return res;
              });

        const productsPromise = window.globalProductCache?.products
          ? Promise.resolve({
              ok: true,
              json: async () => ({ items: window.globalProductCache.products }),
            })
          : fetch('/api/products/products').then(async (res) => {
              if (!res.ok && res.status === 429) {
                await new Promise((resolve) => setTimeout(resolve, 3000));
                return fetch('/api/products/products');
              }
              return res;
            });

        const [pakketRes, allProductsRes] = await Promise.all([
          pakketPromise,
          productsPromise,
        ]);

        if (!pakketRes.ok || !allProductsRes.ok) {
          const errorText = await pakketRes.text();
          throw new Error(
            `API failed: ${pakketRes.status} - ${errorText.substring(0, 100)}`,
          );
        }

        const pakketVersion = pakketRes.headers?.get('X-Configurator-Version');
        const productsVersion = allProductsRes.headers?.get(
          'X-Configurator-Version',
        );

        if (pakketVersion) {
          const versionResult = handleVersionChange(pakketVersion);
          if (versionResult.changed) {
            if (window.globalPackageCache) {
              window.globalPackageCache = {};
            }
          }
        }

        if (productsVersion) {
          const versionResult = handleVersionChange(productsVersion);
          if (versionResult.changed) {
            if (window.globalProductCache) {
              window.globalProductCache = {};
            }
          }
        }

        const [pakketData, allProductsData] = await Promise.all([
          pakketRes.json(),
          allProductsRes.json(),
        ]);

        let processedData;
        try {
          const { processPackageData } = await import(
            './utils/dataProcessor.js'
          );
          processedData = processPackageData(pakketData, allProductsData);
        } catch (processingError) {
          console.warn(
            'Data processing failed, using optimized fallback:',
            processingError,
          );
          const allProducts = allProductsData.items || allProductsData;
          const pakketProducts = pakketData.products || [];
          const pakketProductsMap = new Map(
            pakketProducts.map((p) => [p.id, p]),
          );

          processedData = {
            allProducts: allProducts.map((p) => {
              const pakketProduct = pakketProductsMap.get(p.id);
              const count = pakketProduct ? pakketProduct.count || 1 : 0;
              return {
                ...p,
                imageUrl: p.imageUrl || p.image || '',
                count,
                quantity: count,
              };
            }),
            foodDrinks: (pakketData.foodDrinks || []).map((f) => ({
              ...f,
              imageUrl: f.imageUrl || f.image || '',
              count: f.count || 1,
              quantity: f.count || 1,
            })),
            packaging: pakketData.packaging
              ? {
                  ...pakketData.packaging,
                  id: pakketData.packaging.id,
                  name: pakketData.packaging.name,
                  title:
                    pakketData.packaging.title || pakketData.packaging.name,
                  price: pakketData.packaging.price || 0,
                  price_excl_tax:
                    pakketData.packaging.price_excl_tax ||
                    pakketData.packaging.price ||
                    0,
                  image: pakketData.packaging.image,
                  imageUrl: pakketData.packaging.image,
                  attributes: pakketData.packaging.attributes || [],
                }
              : null,
          };
        }

        configData.value.isBasePackage = true;
        configData.value.wc_tax_settings = pakketData.wc_tax_settings;
        configData.value.basePackage = {
          ...pakket,
          title: pakket.title || pakket.name || 'Pakket',
          wc_tax_settings: pakketData.wc_tax_settings,
          products: (pakketData.products || []).map((p) => ({
            ...p,
            count: p.count || 1,
            quantity: p.count || 1,
          })),
          foodDrinks: (pakketData.foodDrinks || []).map((f) => ({
            ...f,
            count: f.count || 1,
            quantity: f.count || 1,
          })),
          packaging: processedData.packaging,
        };

        configData.value.products = processedData.allProducts;
        configData.value.foodDrinks = processedData.foodDrinks;
        configData.value.packaging = processedData.packaging;

        if (pakketData.size === 'Brievenbus') {
          setMailboxSession();
        }

        if (!window.globalPackageCache) {
          window.globalPackageCache = {};
        }
        window.globalPackageCache[pakket.id] = pakketData;

        if (!window.globalProcessedPackageCache) {
          window.globalProcessedPackageCache = {};
        }
        window.globalProcessedPackageCache[pakket.id] = processedData;

        cleanupUrlParameter('pakket');
        nextTick(() => {
          nextStep();
        });
      } catch (error) {
        console.error('Error during package selection:', error);
        configData.value = originalConfigData;
        showError(
          'Er is een fout opgetreden bij het laden van het pakket. Probeer het opnieuw.',
        );
      } finally {
        selectingPackage.value = false;
      }
    };

    // Intercept cart navigation when in edit mode
    useCartLinkInterceptor(isEditMode, handleAddToCart);

    onMounted(() => {
      // Check if there's a compose flow draft when entering selectPackage flow
      checkForFlowSwitch();
      loadSavedPackageData();
      loadSessionData();
    });

    return {
      packageTypeImageBox,
      packageTypeImageMailbox,
      currentStepIndex,
      configData,
      errors,
      selectingPackage,
      cartItemKey,
      showPackageTypeModal,
      packageType,
      showContinueBanner,
      showAddToCartModal,
      addToCartModalData,
      modalContext,
      draftSummary,
      isSaving,
      lastSaved,
      saveError,
      hasUnsavedChanges,
      flowSteps,
      totalSteps,
      currentStepId,
      normalizedCurrentStep,
      checkForDraft,
      handleContinueDraft,
      handleNewConfiguration,
      handleDismissBanner,
      loadSessionData,
      loadSavedPackageData,
      reEvaluateContext,
      validateConfigData,
      updateData,
      onPrintingUpdated,
      updateProductsFromSidebar,
      updateFoodDrinksFromSidebar,
      handleNextStep,
      nextStep,
      previousStep,
      handleGoToStep,
      increaseProduct,
      decreaseProduct,
      removeProduct,
      removeFoodDrink,
      removePackaging,
      removeCard,
      handleAddToCart,
      resetPackage,
      handlePackageTypeConfirm,
      reloadStepDataWithFilter,
      updateQuantity,
      handleBaseSelection,
      handleCloseModal,
      handleCloseAddToCartModal,
      handleOpenPackageTypeModal,
      handleShowPackageTypeModal,
      clearDraft,
    };
  },
};
</script>
